import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import analyticsReducer from './slices/analyticsSlice';
import {
  activityFeedApiSlice,
  adminFeatureFlagsApiSlice,
  analyticsApiSlice,
  batchDownloadApiSlice,
  billingApiSlice,
  clinicalNotesApiSlice,
  diagnosisCodesApiSlice,
  featureFlagsApiSlice,
  filesApiSlice,
  npiApiSlice,
  organizationApiSlice,
  patientsApiSlice,
  physicianInterpretationApiSlice,
  reportsApiSlice,
  scheduledTransmissionApiSlice,
  supportApiSlice,
  transmissionHistoryApiSlice,
  userApiSlice
} from './slices/api';
import { appointmentApiSlice } from './slices/api/appointmentApiSlice';
import { boxInClinicApiSlice } from './slices/api/boxInClinicApiSlice';
import { patientDocumentsApiSlice } from './slices/api/patient/patientDocumentsApiSlice';
import { patientListApiSlice } from './slices/api/patient/patientListApiSlice';
import { patientRecallsApiSlice } from './slices/api/patient/recallsApiSlice';
import { patientDiagnosesApi } from './slices/api/patientDiagnosesApiSlice';
import { schedulesApiSlice } from './slices/api/schedules/schedulesApiSlice';
import { reportsPatientApiSlice } from './slices/api/transmissionReport/reportsPatientApiSlice';
import { transmissionReportApiSlice } from './slices/api/transmissionReport/transmissionReportsApiSlice';
import authReducer from './slices/authSlice';
import transmissionReportFormReducer from './slices/form/transmissionReportFormSlice';
import patientListFiltersReducer from './slices/patientList/patientListFiltersSlice';
import patientReducer from './slices/patients/patientsSlice';
import preferencesReducer from './slices/preferencesSlice';
import transmissionReportsReducer from './slices/transmissionReports/transmissionReportsSlice';

const logger = createLogger({ collapsed: true });
const additionalMiddlewares: Middleware[] = import.meta.env.DEV ? [] : [];

// Configure persistence for the form slice specifically
const formPersistConfig = {
  key: 'transmissionReportForm',
  storage,
  // Only persist the essential form data
  whitelist: [
    'reportId',
    'batteryStatus',
    'monitoredValues',
    'programmedParameters',
    'counters',
    'inClinicEpisode',
    'inClinicPresenting',
    'notes',
    'dirty',
    'persistenceMetadata',
    'selectedVendors'
  ],
  // Don't persist these calculated/temporary fields
  blacklist: [
    'isInitialized', // Let this reset on refresh - will be recalculated
    'isActiveReportSigned',
    'isEditingSignedReport'
  ],
  version: 1,
  migrate: (state: any) => {
    if (state && !state.persistenceMetadata) {
      state.persistenceMetadata = {
        lastSaveAttempt: null,
        version: 1
      };
    }
    return Promise.resolve(state);
  }
};

const rootPersistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'patients', 'preferences', 'patientListFilters'],
  version: 1
};

// Create persisted form reducer
const persistedFormReducer = persistReducer(formPersistConfig, transmissionReportFormReducer);

const appReducer = combineReducers({
  auth: authReducer,
  patients: patientReducer,
  preferences: preferencesReducer,
  analytics: analyticsReducer,
  transmissionReports: transmissionReportsReducer,
  transmissionReportForm: persistedFormReducer,
  patientListFilters: patientListFiltersReducer,
  [patientsApiSlice.reducerPath]: patientsApiSlice.reducer,
  [patientRecallsApiSlice.reducerPath]: patientRecallsApiSlice.reducer,
  [billingApiSlice.reducerPath]: billingApiSlice.reducer,
  [clinicalNotesApiSlice.reducerPath]: clinicalNotesApiSlice.reducer,
  [filesApiSlice.reducerPath]: filesApiSlice.reducer,
  [userApiSlice.reducerPath]: userApiSlice.reducer,
  [organizationApiSlice.reducerPath]: organizationApiSlice.reducer,
  [reportsApiSlice.reducerPath]: reportsApiSlice.reducer,
  [batchDownloadApiSlice.reducerPath]: batchDownloadApiSlice.reducer,
  [npiApiSlice.reducerPath]: npiApiSlice.reducer,
  [scheduledTransmissionApiSlice.reducerPath]: scheduledTransmissionApiSlice.reducer,
  [featureFlagsApiSlice.reducerPath]: featureFlagsApiSlice.reducer,
  [adminFeatureFlagsApiSlice.reducerPath]: adminFeatureFlagsApiSlice.reducer,
  [transmissionHistoryApiSlice.reducerPath]: transmissionHistoryApiSlice.reducer,
  [diagnosisCodesApiSlice.reducerPath]: diagnosisCodesApiSlice.reducer,
  [analyticsApiSlice.reducerPath]: analyticsApiSlice.reducer,
  [activityFeedApiSlice.reducerPath]: activityFeedApiSlice.reducer,
  [transmissionReportApiSlice.reducerPath]: transmissionReportApiSlice.reducer,
  [patientListApiSlice.reducerPath]: patientListApiSlice.reducer,
  [patientDocumentsApiSlice.reducerPath]: patientDocumentsApiSlice.reducer,
  [schedulesApiSlice.reducerPath]: schedulesApiSlice.reducer,
  [reportsPatientApiSlice.reducerPath]: reportsPatientApiSlice.reducer,
  [supportApiSlice.reducerPath]: supportApiSlice.reducer,
  [physicianInterpretationApiSlice.reducerPath]: physicianInterpretationApiSlice.reducer,
  [appointmentApiSlice.reducerPath]: appointmentApiSlice.reducer,
  [patientDiagnosesApi.reducerPath]: patientDiagnosesApi.reducer,
  [boxInClinicApiSlice.reducerPath]: boxInClinicApiSlice.reducer
});

const rootReducer = persistReducer(rootPersistConfig, appReducer);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionsPaths: ['meta.arg', 'payload.timestamp'],
        ignoredPaths: ['transmissionReportForm.persistenceMetadata.lastSaveAttempt']
      }
    })
      .prepend(
        clinicalNotesApiSlice.middleware,
        reportsApiSlice.middleware,
        patientRecallsApiSlice.middleware,
        filesApiSlice.middleware,
        userApiSlice.middleware,
        organizationApiSlice.middleware,
        scheduledTransmissionApiSlice.middleware,
        batchDownloadApiSlice.middleware,
        npiApiSlice.middleware,
        patientsApiSlice.middleware,
        billingApiSlice.middleware,
        featureFlagsApiSlice.middleware,
        adminFeatureFlagsApiSlice.middleware,
        transmissionHistoryApiSlice.middleware,
        diagnosisCodesApiSlice.middleware,
        analyticsApiSlice.middleware,
        activityFeedApiSlice.middleware,
        supportApiSlice.middleware,
        transmissionReportApiSlice.middleware,
        patientListApiSlice.middleware,
        schedulesApiSlice.middleware,
        reportsPatientApiSlice.middleware,
        physicianInterpretationApiSlice.middleware,
        appointmentApiSlice.middleware,
        patientDiagnosesApi.middleware,
        patientDocumentsApiSlice.middleware,
        boxInClinicApiSlice.middleware
      )
      .concat(additionalMiddlewares);
  },
  devTools: import.meta.env.DEV
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
